import type { estypes } from '@elastic/elasticsearch';

import {
  type AggregatedBettingSplitsResultBody,
  type BettingSplitsAggregations,
  type BettingSplitsBet,
  type BettingSplitsESResult,
  type BettingSplitsESResultBody,
  type BettingSplitsMarket,
  type BettingSplitsResult,
} from '~/apps/product-plus/search-domain/pipelines/betting-splits/types';
import { type Loggable } from '~/apps/product-plus/search-domain/types';
import { type BettingInsightsElasticSearchContext } from '~/apps/product-plus/search-domain/types/contracts';
import { replaceResponseHits } from '~/apps/product-plus/search-domain/utils';
import {
  extractBet,
  extractMarket,
  extractSubevent,
  isMarketSupported,
} from '~/apps/product-plus/search-domain/utils/response-factories';

export type OriginalAggregations = {
  subevents: SubeventsAggregation;
};

export type SubeventsAggregation = {
  buckets: SubeventsBucket[];
};

export type AggregationBucket = {
  key: number;
};

export type SubeventsBucket = AggregationBucket & {
  markets: { buckets: MarketsBucket[] };
};

export type MarketsBucket = AggregationBucket & {
  bets: { buckets: BetsBucket[] };
};

export type BetsBucket = AggregationBucket & {
  best_odds: {
    hits: {
      hits: estypes.SearchHit<BettingSplitsESResult>[];
    };
  };
};

export async function createResponseFactory(
  this: Loggable,
  config: BettingInsightsElasticSearchContext,
  searchResponse: BettingSplitsESResultBody,
  from = 0,
  hitsPerPage = 10,
): Promise<AggregatedBettingSplitsResultBody> {
  const aggregations = searchResponse.aggregations as OriginalAggregations;
  const { subevents: subeventsGroup, ...baseAggregations } = aggregations;
  const { originalHits, subevents } = aggregateSubevents.call(this, subeventsGroup.buckets, config);
  const hits = buildHits(subevents, originalHits);
  const paginatedHits = hits.slice(from, from + hitsPerPage);

  return replaceResponseHits(searchResponse, {
    aggregations: baseAggregations as BettingSplitsAggregations,
    hits: paginatedHits,
    isWalled: config.isWalled,
    totalRelation: 'eq',
    totalValue: hits.length,
  });
}

type AggregationState = {
  originalHits: Map<number, estypes.SearchHit<BettingSplitsESResult>>;
  subevents: Map<number, BettingSplitsResult>;
};

function aggregateSubevents(
  this: Loggable,
  subeventBuckets: SubeventsBucket[],
  config: BettingInsightsElasticSearchContext,
): AggregationState {
  const state: AggregationState = {
    subevents: new Map<number, BettingSplitsResult>(),
    originalHits: new Map<number, estypes.SearchHit<BettingSplitsESResult>>(),
  };

  for (const { key: subeventId, markets } of subeventBuckets) {
    let subevent = state.subevents.get(subeventId);

    for (const { bets, key: marketId } of markets.buckets) {
      let market = subevent?.markets.find((item: BettingSplitsMarket) => item.marketId === marketId);
      for (const { best_odds } of bets.buckets) {
        for (const hit of best_odds.hits.hits) {
          const result = hit._source;
          if (!result) continue;

          subevent = subevent ?? createSubevent.call(this, result, hit, state, config);
          if (!subevent) continue;

          market = market ?? createMarket.call(this, result, marketId, subevent, config);
          if (!market) continue;

          const bet = createBet.call(this, result);
          if (!bet) continue;
          upsertBet(market, bet);
        }
      }

      if (subevent && market && shouldIncludeMarket(market)) {
        subevent.markets.push(market);
      }
    }
  }

  return state;
}

function createSubevent(
  this: Loggable,
  result: BettingSplitsESResult,
  hit: estypes.SearchHit<BettingSplitsESResult>,
  state: AggregationState,
  config: BettingInsightsElasticSearchContext,
): BettingSplitsResult | undefined {
  try {
    const subevent = extractSubevent(result, config.userPreference);
    subevent.markets = [];
    state.subevents.set(result.subeventId, subevent);
    state.originalHits.set(result.subeventId, hit);
    return subevent;
  } catch (error) {
    this.logger.warn(`Failed to parse subevent fields: ${result.subeventId}`, error);
    return undefined;
  }
}

function createMarket(
  this: Loggable,
  result: BettingSplitsESResult,
  marketId: number,
  subevent: BettingSplitsResult,
  config: BettingInsightsElasticSearchContext,
): BettingSplitsMarket | undefined {
  try {
    const market = extractMarket(result);
    if (!isMarketSupported(market, config)) return undefined;
    subevent.betTypeId.push(market.betTypeId);
    market.bets = [];
    return market;
  } catch (error) {
    this.logger.warn(`Failed to parse market fields: ${marketId}`, error);
    return undefined;
  }
}

function createBet(this: Loggable, result: BettingSplitsESResult): BettingSplitsBet | undefined {
  try {
    return extractBet(result);
  } catch (error) {
    this.logger.warn(`Failed to parse bet fields: ${result.betId}`, error);
    return undefined;
  }
}

function upsertBet(market: BettingSplitsMarket, bet: BettingSplitsBet) {
  const existingBetIndex = market.bets.findIndex(({ betId }) => betId === bet.betId);
  if (existingBetIndex === -1) {
    market.bets.push(bet);
    return;
  }
  market.bets[existingBetIndex] = bet;
}

function shouldIncludeMarket(market: BettingSplitsMarket): boolean {
  // Inclusion rules:
  // - Soccer (3-way + Draw): keep market when exactly 2 non-Draw outcomes remain.
  // - American 2-way (non total): require exactly 2 bets.
  // - Over/Under totals: allow partial availability (>=1 bet).
  const nonDrawBets = market.bets.filter(bet => bet.betName !== 'Draw');
  const hasDrawOption = market.bets.some(bet => bet.betName === 'Draw');
  const isOverUnderMarket = market.marketClassification === 'OVER_UNDER';

  return (
    (hasDrawOption && nonDrawBets.length === 2) ||
    (!hasDrawOption && !isOverUnderMarket && market.bets.length === 2) ||
    (!hasDrawOption && isOverUnderMarket && market.bets.length >= 1)
  );
}

function buildHits(
  subevents: Map<number, BettingSplitsResult>,
  originalHits: Map<number, estypes.SearchHit<BettingSplitsESResult>>,
) {
  return Array.from(subevents.values())
    .filter(({ markets }) => markets.length > 0)
    .map(subevent => {
      const originalHit = originalHits.get(subevent.subeventId);
      return {
        ...(originalHit || {}),
        _index: originalHit?._index || '',
        _source: subevent,
      };
    });
}
